import React, {useState, useEffect} from 'react'
import styled from 'styled-components'
import {User} from 'auth/user'
import Page from 'Event/template/NiftyFifty/Page'
import LeftPanel from 'Event/template/NiftyFifty/Dashboard/LeftPanel'
import RightPanel from 'Event/template/NiftyFifty/Dashboard/RightPanel'
import MobilePanel from 'Event/template/NiftyFifty/Dashboard/MobilePanel'
import SpeakerPage from 'Event/template/NiftyFifty/Dashboard/Speakers'
import TabPanel from 'lib/ui/tabs/TabPanel'
import Home from 'Event/template/NiftyFifty/Dashboard/Home'
import Leaderboard from 'Event/template/NiftyFifty/Dashboard/Leaderboard'
import Resources from 'Event/template/NiftyFifty/Dashboard/Resources'
import {
  useNiftyFiftyTemplate,
  useHasMultipleTabs,
} from 'Event/template/NiftyFifty'
import SponsorPage from 'Event/template/NiftyFifty/Dashboard/Sponsors'
import ImageWaterfall from 'Event/template/NiftyFifty/Dashboard/ImageWaterfall'
import FaqPage from 'Event/template/NiftyFifty/Dashboard/Faqs'
import AgendaList from 'Event/template/NiftyFifty/Dashboard/AgendaList'
import {useQueryParams} from 'lib/url'
import {useHistory} from 'react-router-dom'
import {TABS} from 'Event/template/NiftyFifty/Dashboard/RightPanel/Bar'
import qs from 'qs'
import {useEditMode} from 'Event/EditModeProvider'

export default function NiftyFiftyDashboard(props: {user: User}) {
  const [tabIndex, setTabIndex] = useState(0)
  useUrlQueryNav(tabIndex, setTabIndex)

  const setUrlTab = useSetUrlTab()

  const handleChangeTab = (newTab: number) => {
    const isCurrent = newTab === tabIndex
    if (isCurrent) {
      return
    }

    setTabIndex(newTab)
    const tab = TABS[newTab]
    setUrlTab(tab)
  }

  return (
    <Page
      Left={<LeftPanel onChangeTab={handleChangeTab} user={props.user} />}
      Right={
        <RightPanel currentTab={tabIndex} onChangeTab={handleChangeTab}>
          <Content currentTab={tabIndex} />
        </RightPanel>
      }
      Mobile={
        <MobilePanel onChangeTab={handleChangeTab} user={props.user}>
          <Content currentTab={tabIndex} />
        </MobilePanel>
      }
    />
  )
}

/**
 * Navigate the tabs depending on url params, such
 * as `tab=speakers`.
 *
 * @param tabIndex
 * @param setTabIndex
 */
function useUrlQueryNav(
  tabIndex: number,
  setTabIndex: (index: number) => void,
) {
  const {tab: value} = useQueryParams()

  // Select a tab from URL.
  useEffect(() => {
    if (!value) {
      return
    }

    const target = getTabIndex(value)
    const alreadySelected = tabIndex === target
    if (alreadySelected) {
      return
    }

    setTabIndex(target)
  }, [value, setTabIndex, tabIndex])
}

function useSetUrlTab() {
  const history = useHistory()

  return (tab: string) => {
    const query = {
      tab,
    }

    const queryString = qs.stringify(query)
    history.push({
      search: queryString,
    })
  }
}

function getTabIndex(value: string) {
  const index = TABS.indexOf(value)

  // In NiftyFifty, both zoom backgrounds and resources are on resources page.
  // so '?tab=backgrounds' query param means tab=resources
  const isResourceTab = index === -1 && value === 'backgrounds'
  if (isResourceTab) {
    return TABS.indexOf('resources')
  }

  const target = index === -1 ? 0 : index

  return target
}

function Content(props: {currentTab: number}) {
  const {currentTab} = props

  const hasMultipleTabs = useHasMultipleTabs()
  const isEditMode = useEditMode()

  if (!hasMultipleTabs && !isEditMode) {
    return (
      <ContentPanel index={0} currentIndex={0} extraPadding>
        <Home />
      </ContentPanel>
    )
  }

  return (
    <>
      <ContentPanel index={0} currentIndex={currentTab} extraPadding>
        <Home />
      </ContentPanel>
      <ContentPanel index={1} currentIndex={currentTab}>
        <SpeakerPage />
      </ContentPanel>
      <ContentPanel index={2} currentIndex={currentTab}>
        <SponsorPage />
      </ContentPanel>
      <ContentPanel index={3} currentIndex={currentTab}>
        <Resources />
      </ContentPanel>
      <ContentPanel index={4} currentIndex={currentTab} extraPadding>
        <Leaderboard />
      </ContentPanel>
      <ContentPanel index={5} currentIndex={currentTab} extraPadding>
        <ImageWaterfall />
      </ContentPanel>
      <ContentPanel index={6} currentIndex={currentTab}>
        <FaqPage />
      </ContentPanel>
      <ContentPanel index={7} currentIndex={currentTab}>
        <AgendaList />
      </ContentPanel>
    </>
  )
}

function ContentPanel(props: {
  index: number
  currentIndex: number
  children: React.ReactElement
  extraPadding?: boolean
}) {
  const {textColor} = useNiftyFiftyTemplate()

  return (
    <StyledTabPanel
      textColor={textColor}
      index={props.index}
      currentIndex={props.currentIndex}
      extraPadding={props.extraPadding}
    >
      {props.children}
    </StyledTabPanel>
  )
}

const StyledTabPanel = styled(TabPanel)<{
  textColor: string
  extraPadding?: boolean
}>`
  /*
    Explicitly set color to avoid dark mode's white color from
    rendering text invisible on white background.
  */
  color: ${(props) => props.textColor}!important;
  padding: 20px ${(props) => (props.extraPadding ? 35 : 0)}px !important;
`
